import { useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert, App, Button, Modal, Popconfirm, Space } from 'antd'
import dayjs from 'dayjs'
import type { CalendarSendInput } from '@super-mega/server/contracts/bill'
import { useBatchJobCompletion } from '@/hooks/use-batch-job-completion.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getBatchJobStatusLabel } from '@/utils/batch-job.ts'
import { getErrorMessage } from '@/utils/error.ts'

export function CalendarSendAction({ date, count }: { date: string; count: number }) {
  const trpc = useTRPC()
  const cache = useQueryClient()
  const { message } = App.useApp()
  const [open, setOpen] = useState(false)
  const [task, setTask] = useState<{ jobId: string; count: number; date: string }>()
  const pending = useRef<CalendarSendInput | undefined>(undefined)
  const submitting = useRef(false)
  const submit = useMutation(trpc.bill.sendCalendar.mutationOptions())
  const status = useQuery({
    ...trpc.bill.calendarSendStatus.queryOptions({ jobId: task?.jobId ?? '' }),
    enabled: !!task,
    refetchInterval: (query) =>
      ['completed', 'failed', 'missing'].includes(query.state.data?.state ?? '') ? false : 3000
  })
  const state = status.data?.state
  const running = !!task && !['completed', 'failed', 'missing'].includes(state ?? '')
  const results = status.data?.results ?? []
  const queued = results.filter((result) => result.status === 'queued').length
  const grouped = results.filter((result) => result.status === 'grouped').length
  const empty = results.filter((result) => result.status === 'empty').length
  const failed = results.filter((result) => result.status === 'failed').length

  useBatchJobCompletion({
    jobId: task?.jobId,
    state,
    onComplete: async () => {
      await Promise.all([
        cache.invalidateQueries(trpc.bill.list.queryFilter()),
        cache.invalidateQueries(trpc.bill.groupList.queryFilter()),
        cache.invalidateQueries(trpc.bill.candidates.queryFilter()),
        cache.invalidateQueries(trpc.bill.previewCustomerSend.queryFilter()),
        cache.invalidateQueries(trpc.bill.previewMonthlyGroup.queryFilter()),
        cache.invalidateQueries(trpc.bill.automationRuns.queryFilter()),
        cache.invalidateQueries(trpc.bot.wecomDeliveries.queryFilter())
      ])
    },
    onError: () => {
      void message.warning('任务已结束，账单列表刷新失败，请手动刷新')
    }
  })

  const send = async () => {
    if (submitting.current || running) return
    submitting.current = true
    // 入队响应丢失时使用同一个请求编号，避免重复归组和发送。
    pending.current ??= { date, idempotencyKey: crypto.randomUUID() }
    try {
      const result = await submit.mutateAsync(pending.current)
      setTask({ ...result, date: pending.current.date })
      pending.current = undefined
      setOpen(true)
    } catch (error) {
      if (
        error instanceof Error &&
        'data' in error &&
        (error.data as { code?: string } | undefined)?.code === 'CONFLICT'
      ) {
        pending.current = undefined
      }
      void message.error(getErrorMessage(error, '发送任务提交失败，请重试'))
    } finally {
      submitting.current = false
    }
  }

  return (
    <>
      <Space wrap>
        {task && <Button onClick={() => setOpen(true)}>查看发送任务</Button>}
        {submit.isError && pending.current ? (
          <Button loading={submit.isPending} onClick={() => void send()}>
            重试 {pending.current.date} 提交
          </Button>
        ) : (
          <Popconfirm
            title={`发送 ${dayjs(date).format('M 月 D 日')}全部 ${count} 位客户的收款账单？`}
            description={
              <div className="max-w-80">
                按账单创建时间汇总 {dayjs(date).subtract(1, 'month').format('YYYY 年 MM 月')}的已出账待收账单，
                按客户和经营主体归组，同一客户合并为一份 PDF 发到其企微群。
                未绑定群仍生成账单组；已归组账单跳过。处理范围不受当前搜索和分页影响。
              </div>
            }
            okText="生成并发送"
            cancelText="取消"
            onConfirm={send}
            disabled={!count || running || submit.isPending}
          >
            <Button type="primary" loading={submit.isPending} disabled={!count || running}>
              一键发送收款账单
            </Button>
          </Popconfirm>
        )}
      </Space>
      <Modal
        open={open}
        title={`${task?.date ?? date} · 收款账单发送任务`}
        width="48rem"
        onCancel={() => setOpen(false)}
        footer={<Button onClick={() => setOpen(false)}>关闭</Button>}
      >
        <div className="flex flex-col gap-4">
          <Alert
            showIcon
            type={state === 'failed' || failed > 0 ? 'warning' : 'info'}
            title={`任务${getBatchJobStatusLabel(state)}：${status.data?.processed ?? 0}/${task?.count ?? 0} 位客户`}
            description={
              <>
                <p>
                  已提交发送 {queued} 位，仅生成账单组 {grouped} 位，无可归组账单 {empty} 位，失败 {failed} 位。
                </p>
                <p>关闭窗口后任务继续执行。提交发送不代表已送达，实际结果请查看企微发送记录。</p>
                {status.data?.message && <p>{status.data.message}</p>}
              </>
            }
          />
          {status.isError && (
            <Alert
              type="error"
              title={getErrorMessage(status.error, '任务查询失败')}
              action={<Button onClick={() => void status.refetch()}>重试查询</Button>}
            />
          )}
          <ul className="m-0 flex max-h-96 list-none flex-col gap-3 overflow-y-auto p-0" aria-label="客户处理结果">
            {results.map((result) => (
              <li key={result.customerId} className="border-border rounded-md border p-3">
                <div className="font-medium">
                  {result.customerName}（#{result.customerId}）
                </div>
                <div>{result.message}</div>
                {result.groupIds.length > 0 && <div>账单组 ID：{result.groupIds.join('、')}</div>}
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    </>
  )
}
